import React from 'react';
import Head from 'next/head';
import type { StaticImageData } from 'next/image';
import { buildImageSrc } from "./buildImageSrc";
import { extname } from "./extname";
import { fetchPriorityProps } from "./fetchPriority";
import { buildSrcSet, capCandidates, imageCandidates, sourceDimensionsFromSrc } from "./imageCandidates";

export type ImageLoaderType = (params: {
  src: string;
  width: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
  isProduction?: boolean;
  basePath?: string;
  imgproxyPort: string;
}) => string;

interface StaticRequire {
  default: StaticImageData;
}

type InternalStaticImport = StaticRequire | StaticImageData;

interface PublicOptimizedImageProps {
  src: string | InternalStaticImport;
  /** Largura de exibição, em px. Com `height`, define a proporção da caixa. */
  width: number;
  height: number;
  /**
   * Recortar a imagem na proporção `width`×`height` no imgproxy. Default:
   * `false` — o arquivo mantém a proporção da origem, a caixa fica na proporção
   * `width`×`height` (`aspect-ratio`) e o `object-fit` do consumidor decide como
   * a imagem ocupa a caixa, como no `next/image` e no `next/legacy/image` da 3.x.
   * Ligue para cortar no servidor e baixar menos (fotos de card, avatares); não
   * ligue em imagem que não pode perder borda (mapas, logos, prints de tela).
   */
  crop?: boolean;
  /**
   * Como o `sizes` do `next/image`. Sem ele, a imagem é de largura fixa e o
   * srcset sai com 1x e 2x; com ele (`"100vw"`, `"(min-width: 768px) 50vw,
   * 100vw"`), sai com as larguras da config do projeto e descritor `w`.
   */
  sizes?: string;
  /** Qualidade pedida ao imgproxy. Default: 80. */
  quality?: number;
  /**
   * `'blur'` mostra o LQIP de `blurDataURL` (borrado, via `background-image`)
   * até a imagem real carregar. `'empty'` (default) não mostra nada — é o que
   * deixa a caixa visualmente vazia nos primeiros centenas de ms.
   */
  placeholder?: 'blur' | 'empty';
  /**
   * Data URI do LQIP. O `changeDataByLocal` já devolve um por imagem do CMS
   * (campo `blurDataURL`), gerado pelo imgproxy na proporção da imagem real —
   * basta repassar. Sem ele, `placeholder='blur'` cai num cinza neutro embutido.
   */
  blurDataURL?: string;
  alt: string;
  /**
   * @deprecated Ignorada. Nunca foi repassada a lugar nenhum (nem até a 3.x,
   * quando o componente usava `next/legacy/image`); continua aceita só para
   * não quebrar quem a passa. Use `style`/`className`.
   */
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
  className?: string;
  style?: React.CSSProperties;
  /**
   * Imagem acima da dobra (candidata a LCP). Default: `false`.
   *
   * Com `true`, emite um `<link rel="preload" type="image/avif">` com
   * `imagesrcset` igual, caractere por caractere, ao do `<source>` avif — o
   * navegador reaproveita esse download em vez de baixar duas vezes — e o
   * `<img>` sai com `fetchpriority="high"`. Sem ela, o `<img>` sai com
   * `loading="lazy"`. Ver TD-3 em docs/tech-debts.md.
   */
  priority?: boolean;
  loader: ImageLoaderType;
  isProduction?: boolean;
  basePath?: string;
  imgproxyPort: string;
  /**
   * @deprecated AMP saiu na 4.0.0 (o Next 16 removeu o suporte). Aceita só
   * `false`, para não quebrar quem já passa `isAmp={false}`.
   */
  isAmp?: false;
  /**
   * Formato do `<img>` de fallback do `<picture>` — o que só é usado por
   * navegador que não decodifica avif nem webp. Default: `jpg` para origem
   * jpg/jpeg, `png` para todo o resto. Só sobrescreva se souber que o png é
   * menor para as SUAS imagens (arte de poucas cores, captura de tela).
   */
  fallbackFormat?: 'jpg' | 'png';
}

/**
 * Formato universal do `<img>` de fallback.
 *
 * Até a 2.0.0 isto era a constante `'avif'`: o `<img>` que existe justamente
 * para o navegador sem avif/webp também pedia avif, então esse navegador não
 * exibia imagem nenhuma — sem erro e sem log.
 *
 * A decisão é derivada só da extensão da origem, de propósito: este componente
 * renderiza no servidor E hidrata no cliente, e o loader roda nos dois lados.
 * O caminho do arquivo otimizado embute o formato (`.../optimized/foo-828w.jpg`),
 * então servidor e cliente precisam chegar ao MESMO formato — o cliente não tem
 * disco para consultar. Ver a nota sobre comparação empírica por tamanho no
 * CHANGELOG da 2.0.0.
 *
 * `png` é o default para tudo que não é jpg porque é o universal que preserva
 * canal alfa: escolher jpg para uma origem com transparência real achataria o
 * fundo em branco, silenciosamente.
 */
export const universalFallbackFormat = (ext: string): 'jpg' | 'png' =>
  ext === 'jpg' || ext === 'jpeg' ? 'jpg' : 'png';

const getExtension = (filename: string): string =>
  extname(filename).toLowerCase().replace('.', '');

/**
 * LQIP neutro usado quando se pede `placeholder='blur'` sem `blurDataURL`.
 *
 * Até a 2.0.1 este mesmo base64 era passado como VALOR da prop `placeholder`
 * do `<Image>`, e não como `blurDataURL`, e o borrão nunca era desenhado:
 * pedir blur sem blurDataURL resultava em caixa vazia, sem erro e sem log — o
 * mesmo sintoma que o placeholder existe para evitar.
 */
export const fallbackBlurDataURL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACoAAAAqCAYAAADFw8lbAAAACXBIWXMAAAB2AAAAdgFOeyYIAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAELSURBVHgB7ZVNb8IwDIZtN2yAtrWrtJ72/3/StJ8AHGhVviGxiTgifIFIycHvKVKfWlajvg/yhf/7RfjZDb5BQJnV1dj+uiUiCDyIeKEcPG0G7vZjaIWBmKU6jP5724cOlOTi6bTlr7cpgpsgzD4J3DvBvg+NNigX7+LWGLcHQLjdRbwSIIeiDcrF07ymwR8Z/FnguGG4xPNkiqM2KBdPH61bzeuqJ8KABBzP66ZzS21QLh5F5O/RC7n+bo0nUFJaG6iLltYGTntQWhuoX7S0NlAXLa0N0FyfmDfXp+bN9al5c725/j7m+id5c31q3lyfmjfXp+bN9eb6+5jrn+TN9eZ6c7253lz/Gn8FCzYy28N0VTgAAAAASUVORK5CYII=';

/**
 * `<picture>` com `<source>` avif e webp e um `<img>` de fallback.
 *
 * Até a 3.x o fallback era um `next/legacy/image`, que embrulhava o `<img>` em
 * `<span>`s de dimensionamento — conteúdo inválido dentro de `<picture>` — e
 * cujas larguras não batiam com as dos `<source>`, montados à mão (w, 1,5w, 2w).
 * Agora as larguras dos três vêm do `getImageProps` do Next, com a config de
 * imagens do projeto, e cada largura é um arquivo de verdade (TD-1 e TD-4).
 */
const PublicOptimizedImage: React.FC<PublicOptimizedImageProps> = ({
  src,
  width,
  height,
  sizes,
  quality,
  crop = false,
  placeholder = 'empty',
  blurDataURL,
  alt,
  priority = false,
  loader,
  fallbackFormat,
  isProduction,
  basePath,
  imgproxyPort,
  className,
  style,
}) => {
  // Os hooks ficam antes dos `return` antecipados: a ordem não pode variar.
  const [blurComplete, setBlurComplete] = React.useState(false);
  // Uma imagem já em cache termina de carregar ANTES da hidratação, e nesse
  // caso o `onLoad` do React nunca dispara — o borrão ficaria para sempre.
  const imgRef = React.useCallback((img: HTMLImageElement | null) => {
    if (img?.complete) setBlurComplete(true);
  }, []);

  if (!src) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', fontWeight: 'bold', width: '100%', height: '100%', background: 'red' }}>
        Error: No src!
      </div>
    );
  }

  const normalizedSrc = typeof src === 'string' ? src : 'default' in src ? src.default.src : src.src;

  if (!width || !height) {
    console.error(`[WpTools] OptimizeImage sem width/height (${width}x${height}): sem srcset para ${normalizedSrc}`);
    return <img alt={alt} src={buildImageSrc(normalizedSrc)} className={className} style={style} />;
  }

  const resolvedFallbackFormat: 'jpg' | 'png' = fallbackFormat ?? universalFallbackFormat(getExtension(normalizedSrc));
  // A dimensão da origem vem do nome do arquivo (`…-954x357.png`, gravado pelo
  // `changeDataByLocal`) ou do import estático: os dois lados — build e
  // navegador — chegam à mesma, e portanto ao mesmo srcset.
  const source = typeof src === 'string'
    ? sourceDimensionsFromSrc(normalizedSrc)
    : 'default' in src ? src.default : src;
  // Maior largura que sai sem ampliar: a da origem, ou menos, se o recorte for
  // numa proporção mais larga que ela.
  const maxWidth = source?.width && source?.height
    ? crop ? Math.min(source.width, Math.floor((source.height * width) / height)) : source.width
    : undefined;
  const candidates = capCandidates(imageCandidates({ width, height, sizes }), { displayWidth: width, maxWidth });
  // Sem `sizes`, o Next usa descritor `x` e não emite `sizes`. A exceção é a
  // origem menor que a largura declarada: o candidato único vem com descritor
  // `w` (ver `capCandidates`), e o slot é a própria largura declarada — é o que
  // mantém a largura intrínseca do `<img>` em `width`, e não na do arquivo.
  const sizesAttr = sizes ?? (candidates[0].descriptor.endsWith('w') ? `${width}px` : undefined);

  const urlFor = (format: 'avif' | 'webp' | 'jpg' | 'png') => (w: number) =>
    loader({
      src: normalizedSrc,
      width: w,
      // Só com `crop`: recorte na proporção pedida pelo componente.
      height: crop ? Math.round((w * height) / width) : undefined,
      quality,
      format,
      isProduction,
      basePath,
      imgproxyPort,
    });

  const fallbackUrlFor = (w: number) => {
    try {
      return urlFor(resolvedFallbackFormat)(w);
    } catch (err) {
      // `error`: o fallback sai com a URL original, não a otimizada (TD-4).
      console.error('OptimizeImage loader fallback:', err);
      return buildImageSrc(normalizedSrc);
    }
  };

  // Gerado uma vez e usado no `<source>` e no preload: o `imagesrcset` do
  // preload só é reaproveitado se for o MESMO texto do srcset escolhido.
  const avifSrcSet = buildSrcSet(candidates, urlFor('avif'));
  const webpSrcSet = buildSrcSet(candidates, urlFor('webp'));
  const fallbackSrcSet = buildSrcSet(candidates, fallbackUrlFor);
  // Como o `next/image`: o `src` é a maior largura, para quem ignora srcset.
  const fallbackSrc = fallbackUrlFor(candidates[candidates.length - 1].width);

  const showBlur = placeholder === 'blur' && !blurComplete;
  const blurStyle: React.CSSProperties = showBlur
    ? {
        backgroundImage: `url("${blurDataURL || fallbackBlurDataURL}")`,
        backgroundSize: 'cover',
        backgroundPosition: '0% 0%',
        filter: 'blur(20px)',
      }
    : {};

  return (
    <>
      {priority && (
        <Head>
          {/* `type` impede o navegador sem avif de baixar o arquivo errado; o
              `key` evita preload duplicado se a mesma imagem aparecer duas vezes. */}
          <link
            key={`wp-preload:${avifSrcSet}`}
            rel='preload'
            as='image'
            type='image/avif'
            imageSrcSet={avifSrcSet}
            imageSizes={sizesAttr}
            {...fetchPriorityProps('high')}
          />
        </Head>
      )}
      <picture>
        <source type='image/avif' srcSet={avifSrcSet} sizes={sizesAttr} />
        <source type='image/webp' srcSet={webpSrcSet} sizes={sizesAttr} />
        <img
          alt={alt}
          width={width}
          height={height}
          decoding='async'
          // No `<img>` de um `<picture>`, `loading` e `fetchpriority` valem
          // para o `<source>` escolhido.
          loading={priority ? undefined : 'lazy'}
          {...fetchPriorityProps(priority ? 'high' : undefined)}
          ref={imgRef}
          onLoad={() => setBlurComplete(true)}
          // Se a imagem real falhar, o borrão tem que sair mesmo assim.
          onError={() => setBlurComplete(true)}
          className={className}
          style={{
            // O que o `layout="intrinsic"` (default do legacy) fazia com os
            // `<span>`: nunca passa da largura do container, a altura
            // acompanha, e a caixa fica na proporção `width`×`height` — sem
            // `crop`, o arquivo tem a proporção da origem, e o `object-fit` do
            // consumidor decide. Antes dos estilos do consumidor, para ele
            // sobrescrever.
            maxWidth: '100%',
            height: 'auto',
            aspectRatio: `${width} / ${height}`,
            ...blurStyle,
            ...style,
          }}
          sizes={sizesAttr}
          srcSet={fallbackSrcSet}
          // `src` por último, como o `next/image`: o Safari começa a baixar o
          // `src` assim que o vê, antes de `srcSet`/`sizes`.
          src={fallbackSrc}
        />
      </picture>
    </>
  );
};

PublicOptimizedImage.displayName = 'OptimizeImage';
export default PublicOptimizedImage;
